import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthenticationHook } from 'src/API/rest.authentication';
import { ButtonIconSquare, ButtonRounded } from 'src/components/buttons';
import { InputOTPDemo } from 'src/components/inputotp';
import { cn } from 'src/lib/utils';
import { ToastAction } from 'src/shadcn/ui/toast';
import { toast } from 'src/shadcn/ui/use-toast';
import { IconDatabase, IconBox, IconChartBar, IconAdjustments, IconBrain, IconUserCheck } from '@tabler/icons-react';

export default function Loginpage() {
  const navigate = useNavigate();
  const { requestOTP, verifyOTP } = useAuthenticationHook();
  const [mode, setMode] = useState<'otp' | 'username'>('username');
  const [inputUsername, setInputUsername] = useState('');
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [email, setEmail] = useState(localStorage.getItem('email') || '');
  const [isLoading, setIsLoading] = useState(false);

  const otpInputRef = useRef<HTMLInputElement>(null);

  const handleSubmitUsername = async (e: React.FormEvent) => {
    e?.preventDefault();
    setIsLoading(true);
    const res = await requestOTP({ username: inputUsername });

    if (res.success === true) {
      const emailParts = res.email.split('@');
      const maskedEmail = `***${emailParts[0].slice(-5)}@${emailParts[1]}`;
      toast({
        title: "Success",
        description: `OTP sent to ${maskedEmail}. Please check your email.`,
      });
      setEmail(res.email);
      setIsOtpSent(true);
    } else {
      setIsLoading(false);
      toast({
        title: "Error",
        description: res.message || "User not found. Please try again.",
      });
    }
  };

  const handleSubmitOtp = async (e: React.FormEvent) => {
    e?.preventDefault();
    const response = await verifyOTP({ email, otp: Number(otp), userName: inputUsername });

    if (response.success === true) {
      navigate('/home/dashboard');
      window.location.reload();
    } else {
      toast({
        title: "Invalid OTP",
        description: response.message || "The OTP is invalid. Please try again.",
        action: <ToastAction className="bg-white" altText="Try again" onClick={() => setOtp('')}>Try again</ToastAction>,
      });
    }
  };

  useEffect(() => {
    if (isOtpSent && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [isOtpSent]);

  return (

    <div className="min-h-screen flex flex-col md:flex-row bg-white ">
      {/* Left Side - Design Layer */}
      <div className=" md:w-1/2 p-8 flex flex-col justify-center items-center bg-gradient-to-b from-[#a0191b] to-[#331111] text-white">
        <h1 className="text-3xl font-bold mb-12 tracking-wide">Empower Your Decisions</h1>

        {/* Base Layer */}
        <div className="mb-16 flex flex-col items-center">
          <h2 className="text-xl font-semibold mb-4">Foundation</h2>
          <div className="flex gap-4">
            <div className="flex flex-col items-center">
              <IconDatabase size={32} className="text-red-200" />
              <span className="text-sm mt-2">ERP</span>
            </div>
            <div className="flex flex-col items-center">
              <IconBox size={32} className="text-red-200" />
              <span className="text-sm mt-2">CRM</span>
            </div>
            <div className="flex flex-col items-center">
              <IconChartBar size={32} className="text-red-200" />
              <span className="text-sm mt-2">Market Data</span>
            </div>
            <div className="flex flex-col items-center">
              <IconDatabase size={32} className="text-red-200" />
              <span className="text-sm mt-2">Historical Data</span>
            </div>
          </div>
        </div>

        {/* Middle Layers */}
        <div className="mb-16 flex flex-col items-center">
          <h2 className="text-xl font-semibold mb-4">Processing</h2>
          <div className="flex gap-4">
            <div className="flex flex-col items-center">
              <IconBrain size={32} className="text-red-300" />
              <span className="text-sm mt-2">AI/ML</span>
            </div>
            <div className="flex flex-col items-center">
              <IconChartBar size={32} className="text-red-300" />
              <span className="text-sm mt-2">Scenario Planning</span>
            </div>
            <div className="flex flex-col items-center">
              <IconAdjustments size={32} className="text-red-300" />
              <span className="text-sm mt-2">Adjustments</span>
            </div>
          </div>
        </div>

        {/* Top Layer */}
        <div className="flex flex-col items-center">
          <h2 className="text-xl font-semibold mb-4">Decisions</h2>
          <div className="flex flex-col items-center">
            <IconUserCheck size={48} className="text-red-100" />
            <span className="text-sm mt-2">Business Leader Dashboard</span>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full md:w-1/2 flex items-center justify-center backdrop-blur-xl" style={{ backgroundImage: `url(/assets/images/imagebglogin.jpg)` }}>
        <div className="bg-[white] flex flex-col justify-center p-8 w-full h-full">
          <div className="flex justify-center items-center flex-col">
            <img className="h-20" src="https://www.vstar.in/dist/logo-new-tcQ.svg" alt="Logo" />
            <img className='max-h-80' src="./assets/images/loginimg.png" alt="" />
          </div>

          <form
            className="flex flex-col gap-8 items-center"
            onSubmit={(e) => {
              if (!isOtpSent) {
                handleSubmitUsername(e);
              } else {
                handleSubmitOtp(e);
              }
            }}
          >

            <input
              className={cn(
                'w-[80%] h-10 bg-white text-black px-4 rounded-lg text-lg font-medium tracking-wide outline-none placeholder:text-gray-400 border border-gray-300 shadow-sm focus:border-[#c00d10] focus:ring-2 focus:ring-[#c00d10] focus:ring-opacity-50 transition-all duration-200',
                isOtpSent && 'hidden'
              )}
              name="usernamevstar"
              placeholder="Enter Username"
              type="text"
              value={inputUsername}
              onChange={(e) => setInputUsername(e.target.value)}
              autoComplete="usernamevstar"
            />

            <button
              className={cn(
                'bg-[#982325] hover:bg-[#5c1e1f] cursor-pointer text-white max-w-60 w-40 md:min-w-60 items-center font-medium py-2 rounded-md transition-all duration-300',
                isOtpSent && 'hidden'
              )}
              type="submit"
            >
              {isLoading ? 'Loading...' : 'Send OTP'}
            </button>

            {isOtpSent && (
              <>
                <InputOTPDemo ref={otpInputRef} value={otp} onChange={setOtp} />

                <button
                  // variant="secondary"

                  className="bg-[#982325] hover:bg-[#5c1e1f] cursor-pointer text-white max-w-40 min-w-40  font-medium py-2 rounded-md transition-all duration-300"
                > Submit OTP</button>
                <button
                  type="button"
                  className="text-sm  -mt-6 text-red-900 underline hover:text-gray-700"
                  onClick={() => {
                    setIsOtpSent(false);
                    setIsLoading(false);
                    setOtp('');
                  }}
                >
                  Change Username
                </button>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}